import { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize2, Download } from "lucide-react";
import { audioUrl, videoUrl, posterUrl } from "@/lib/api";

function fmt(t) {
  if (!Number.isFinite(t)) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function MoviePlayer({ movie }) {
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => {
      setProgress(v.currentTime);
      setDuration(v.duration || 0);
    };
    const onEnd = () => {
      setPlaying(false);
      if (audioRef.current) audioRef.current.pause();
    };
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("loadedmetadata", onTime);
    v.addEventListener("ended", onEnd);
    return () => {
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("loadedmetadata", onTime);
      v.removeEventListener("ended", onEnd);
    };
  }, []);

  const togglePlay = () => {
    const v = videoRef.current;
    const a = audioRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      if (a && movie.has_audio) {
        a.currentTime = v.currentTime;
        a.play().catch(() => {});
      }
      setPlaying(true);
    } else {
      v.pause();
      if (a) a.pause();
      setPlaying(false);
    }
  };

  const toggleMute = () => {
    const v = videoRef.current;
    const a = audioRef.current;
    const next = !muted;
    if (v) v.muted = next;
    if (a) a.muted = next;
    setMuted(next);
  };

  const seek = (e) => {
    const v = videoRef.current;
    const a = audioRef.current;
    if (!v || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    const t = pct * duration;
    v.currentTime = t;
    if (a) a.currentTime = t;
    setProgress(t);
  };

  const goFullscreen = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.requestFullscreen) v.requestFullscreen();
  };

  const pct = duration ? (progress / duration) * 100 : 0;

  return (
    <div data-testid="movie-player" className="group relative w-full border border-white/10 bg-black">
      <div className="relative w-full">
        <video
          ref={videoRef}
          data-testid="movie-video-el"
          src={videoUrl(movie.id)}
          poster={movie.has_poster ? posterUrl(movie.id) : undefined}
          className="block w-full"
          playsInline
          muted
          preload="metadata"
        />
        {movie.has_audio && (
          <audio
            ref={audioRef}
            src={movie._audioBust ? `${audioUrl(movie.id)}?t=${movie._audioBust}` : audioUrl(movie.id)}
            preload="metadata"
          />
        )}

        {!playing && (
          <button
            data-testid="player-big-play"
            onClick={togglePlay}
            className="absolute inset-0 flex items-center justify-center bg-black/40 transition-colors duration-300 hover:bg-black/25"
            aria-label="Play"
          >
            <span className="flex h-20 w-20 items-center justify-center border border-white/70 bg-black/40 backdrop-blur-md transition-transform duration-300 hover:scale-105 hover:border-[#E50914]">
              <Play className="h-8 w-8 translate-x-0.5 text-white" strokeWidth={1.5} fill="currentColor" />
            </span>
          </button>
        )}
      </div>

      <div className="border-t border-white/10 bg-black px-4 py-3">
        <div data-testid="player-seek" className="mb-3 h-[3px] w-full cursor-pointer bg-white/10" onClick={seek}>
          <div className="h-full bg-[#E50914]" style={{ width: `${pct}%` }} />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button data-testid="player-play-toggle" onClick={togglePlay} className="text-white/80 hover:text-white" aria-label={playing ? "Pause" : "Play"}>
              {playing ? <Pause className="h-5 w-5" strokeWidth={1.5} /> : <Play className="h-5 w-5" strokeWidth={1.5} />}
            </button>
            <button data-testid="player-mute-toggle" onClick={toggleMute} className="text-white/80 hover:text-white" aria-label={muted ? "Unmute" : "Mute"}>
              {muted ? <VolumeX className="h-5 w-5" strokeWidth={1.5} /> : <Volume2 className="h-5 w-5" strokeWidth={1.5} />}
            </button>
            <span className="font-mono-ui text-xs tracking-widest text-white/60">{fmt(progress)} / {fmt(duration)}</span>
          </div>

          <div className="flex items-center gap-4">
            <a data-testid="player-download-video" href={videoUrl(movie.id)} download={`${movie.title || "reel"}.mp4`}
               className="flex items-center gap-2 border border-white/15 px-3 py-1.5 font-mono-ui text-[10px] uppercase tracking-[0.28em] text-white/80 transition-colors duration-200 hover:border-[#E50914] hover:text-white">
              <Download className="h-3.5 w-3.5" strokeWidth={1.5} /> MP4
            </a>
            {movie.has_audio && (
              <a data-testid="player-download-audio" href={audioUrl(movie.id)} download={`${movie.title || "reel"}-score.mp3`}
                 className="flex items-center gap-2 border border-white/15 px-3 py-1.5 font-mono-ui text-[10px] uppercase tracking-[0.28em] text-white/80 transition-colors duration-200 hover:border-[#E50914] hover:text-white">
                <Download className="h-3.5 w-3.5" strokeWidth={1.5} /> SCORE
              </a>
            )}
            <button data-testid="player-fullscreen" onClick={goFullscreen} className="text-white/80 hover:text-white" aria-label="Fullscreen">
              <Maximize2 className="h-5 w-5" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
